import { useState } from 'react';
import { TextInput, Button, PasswordInput, Space, Center, Text, createStyles, Alert, Checkbox, Anchor } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { createAuthUserWithEmailAndPassword, createUserDocumentFromAuth } from '../../utils/firebase/firebase.utils';
import {  validateEmail, validatePassword,beigeColor } from '../../assets/constants';
import { useJuneProvider } from '../../contexts/analytics.context';
import FormPaper from '../../components/form-paper/form-paper.component';
import { useUserAuth } from '../../contexts/user.context';
import { IconUserPlus } from '@tabler/icons-react';
import LogoWithText from '../../components/logo-with-text/logo-with-text.component';

const useStyles = createStyles((theme) => ({
    outerContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: beigeColor,
        [theme.fn.smallerThan('xs')]: {
            alignItems: 'start',
            background:'white'
        }
    },

    formContainer: {
        width: '100%',
        maxWidth: '460px',
    },

    submitButton: {
        width: '100%'
    },

    linkText: {
        textDecoration: 'none',
        fontWeight: 500,
        color: '#1959b0',
        '&:hover': {
            textDecoration: 'underline',
        }
    },

    mobileOnly: {
        [theme.fn.largerThan('sm')]: {
            display: 'none',
        }
    },
}));

const SignUp = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [signUpProblem, setSignUpProblem] = useState(false);
    const { classes } = useStyles();
    const analytics = useJuneProvider();
    const { setSubscribeToEmails,setNumCreditsAvailable, setUserAuthStateIsChanging } = useUserAuth();

    const form = useForm({
        initialValues: {
            email: '',
            password: '',
            confirmPassword: '',
            subscribeToEmails: false
        },

        validate: {
            email: (value) => validateEmail(value) ? null : 'Invalid email',
            password: (value) => validatePassword(value),
            confirmPassword: (value, values) => value !== values.password ? 'Passwords do not match' : null,
        },
    });

    const submitHandler = async (event) => {
        event.preventDefault();
        setSignUpProblem(false);
        form.validate();
        if (!form.isValid()) return;
        setIsLoading(true);
        setUserAuthStateIsChanging(true);
        try {
            const { email, password, subscribeToEmails } = form.values;
            const user = await createAuthUserWithEmailAndPassword(email, password);
            await createUserDocumentFromAuth(user, subscribeToEmails);
            // await sendAuthUserEmailVerification(user);
            setSubscribeToEmails(subscribeToEmails);
            setNumCreditsAvailable(20);
            const userCreditResults = await fetch("/.netlify/functions/setUpUserCredits",{method:"POST",body: JSON.stringify({userId: user.uid})});
            const { success } = await userCreditResults.json();
            if (!success) {
                form.setFieldError('email',"There was a problem setting up your account. Please contact us at support@roundtable.ai");
                setIsLoading(false);
                return;
            }
            if (analytics) {
                analytics.identify(user.uid, { email });
                analytics.track({ userId: user.uid, event: 'Signed up' });
                analytics.track({ userId: user.uid, event: 'Signed in' });
            }
            setUserAuthStateIsChanging(false);
        } catch (error) {
            if (error.code === 'auth/email-already-in-use') {
                form.setFieldError('email', 'Email already in use');
            } else {
                console.log("error",error)
                setSignUpProblem(true);
            }
        }
        setIsLoading(false);
    }

    return (
        <>
            <Helmet>
                <title>Sign up | Roundtable</title>
                <meta
                    name='description'
                    content='Sign up for Roundtable and get started with our data cleaning API.'
                />
            </Helmet>
            <div className={classes.outerContainer}>
                <div className={classes.formContainer}>
                    <FormPaper>
                        <Space h='lg' className={classes.mobileOnly} />
                        <Center>
                        <LogoWithText innerWidth = {200} innerHeight = {0} opacity={1} logoScalingFactor={1.1}/>
                        </Center>
                        <Space h='lg' />
                        <Text align="center" size='lg' weight={400}>Create an account</Text>
                        <Text align='center' size='sm'>or <Text component={Link} to='/sign-in' className={classes.linkText} >sign in</Text></Text>
                        <Space h='md' />
                        <form onSubmit={(event) => submitHandler(event)}>
                            <TextInput
                                required
                                label="Email"
                                {...form.getInputProps('email')}
                                size='md'
                                radius = 'md'
                                name='email'
                            />
                            <Space h='sm' />
                            <PasswordInput
                                required
                                label="Password"
                                {...form.getInputProps('password')}
                                size='md'
                                radius = 'md'
                                name='password'
                            />
                            <Space h='sm' />
                            <PasswordInput
                                required
                                label="Confirm your password"
                                {...form.getInputProps('confirmPassword')}
                                size='md'
                                radius = 'md'
                                name='confirmPassword'
                            />
                            <Space h='md' />
                            <Space h='md' />
                            <Checkbox
                                label={
                                    <> 
                                    I agree to the Roundtable <Anchor className = {classes.linkText} href="https://app.roundtable.ai/terms" target="_blank"> Terms of Service</Anchor> and <Anchor className = {classes.linkText} href="https://app.roundtable.ai/privacy" target="_blank"> Privacy Policy</Anchor>
                                    </>
                                }
                                size='md'
                                required
                            />
                            <Space h='lg' />
                            <Checkbox
                                {...form.getInputProps('subscribeToEmails', { type: 'checkbox' })}
                                label="Get updates on new releases"
                                size='md'
                            />
                            <Space h='md' />
                            <Space h='md' />
                            <Button
                                type='submit'
                                size='lg'
                                radius = 'md'
                                className={classes.submitButton}
                                leftIcon={isLoading === false && <IconUserPlus stroke={1.5}/>}
                                loading={isLoading}
                            >
                                {isLoading ? '' : 'Sign up'}
                            </Button>
                        </form>
                        <Space h='xs' />
                    </FormPaper>
                    {
                        signUpProblem &&
                        <>
                            <Alert title="Oops!" color="red" radius='lg'>There was a problem setting up your account. Please try again or <a href='mailto:support@roundtable.ai' target='_blank'>contact support</a>.</Alert>
                        </>
                    }
                </div>
            </div>
        </>
    );
}

export default SignUp;